import { useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";
import { isAuthError } from "@/lib/aws-amplify/auth";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info/_hooks/use-toast";

/**
 * Confirm 時にユーザーから入力を受け取るパラメーター
 */
export type ConfirmResetPasswordParams = {
	email: string;
	password: string;
	confirmationCode: string;
};

export const useResetPassword = () => {
	const navigate = useNavigate();

	const resetPassword = ({ email, password, confirmationCode }: ConfirmResetPasswordParams) =>
		AuthWrapper.authConfirmResetPassword(email, password, confirmationCode)
			.then(() => {
				notifyUserEventInfo({
					variant: "default",
					title: "パスワードを再設定しました",
					description: "続けてサインインをしてください。",
				});
				navigate(PATHS.AUTH.SIGN_IN);
			})
			.catch((err) => {
				const result = isAuthError(err);
				if (result) {
					/**
					 *
					 * @description
					 * amplify/auth から throw されるエラーは以下、
					 * 1. ConfirmForgotPasswordException
					 * 2. AuthValidationErrorCode @see {@link(https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmSignUp.html)}
					 * 3. AuthTokenConfigException
					 *
					 * 2,3 はクライアントでハンドリングしているため、1のみ考慮する。
					 */
					const errorType = err?.name;
					if (
						errorType === "TooManyFailedAttemptsException" ||
						errorType === "TooManyRequestsException" ||
						errorType === "LimitExceededException"
					) {
						notifyUserEventInfo({
							variant: "error",
							title: "パスワードの再設定に失敗しました",
							description: "時間を空けて、もう一度お試しください。",
						});
					} else if (errorType === "ExpiredCodeException") {
						notifyUserEventInfo({
							variant: "error",
							title: "パスワードの再設定に失敗しました",
							description: "再設定済みです。サインインをしてください。",
						});
						navigate(PATHS.AUTH.SIGN_IN);
					} else if (errorType === "CodeMismatchException") {
						notifyUserEventInfo({
							variant: "error",
							title: "パスワードの再設定に失敗しました",
							description: "認証情報が不正です。カスタマーサポートへお問い合わせください。",
						});
					} else {
						notifyUserEventInfo({
							variant: "error",
							title: "パスワードの再設定に失敗しました",
							description: "カスタマーサポートへお問い合わせください。",
						});
					}
				} else {
					notifyUserEventInfo({
						variant: "error",
						title: "パスワードの再設定に失敗しました",
						description: "もう一度お試しください。",
					});
				}
			});

	return {
		resetPassword,
	};
};
